import { useMemo, useState } from "react";
import type { AuditorSubmittedInvoice } from "../../data/auditorDashboard";
import { AuditorDashboardInvoiceFilters } from "./AuditorDashboardInvoiceFilters";
import {
  AuditorSubmittedInvoicesTable,
  parseAuditorSubmittedDate,
} from "./AuditorSubmittedInvoicesTable";

type AuditorDashboardInvoicesSectionProps = {
  invoices?: readonly AuditorSubmittedInvoice[];
};

export function AuditorDashboardInvoicesSection({
  invoices = [],
}: AuditorDashboardInvoicesSectionProps) {
  const [search, setSearch] = useState("");
  const [hospital, setHospital] = useState("");
  const [company, setCompany] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const filteredInvoices = useMemo(() => {
    return invoices.filter((invoice) => {
      const haystack =
        `${invoice.displayId} ${invoice.hospital} ${invoice.company}`.toLowerCase();
      const bySearch = search ? haystack.includes(search.toLowerCase()) : true;
      const byHospital = hospital ? invoice.hospitalKey === hospital : true;
      const byCompany = company ? invoice.companyKey === company : true;

      const submittedDate = parseAuditorSubmittedDate(invoice.submittedDate);
      const fromDate = dateFrom ? new Date(dateFrom) : null;
      const toDate = dateTo ? new Date(dateTo) : null;
      const byFrom = fromDate && submittedDate ? submittedDate >= fromDate : true;
      const byTo = toDate && submittedDate ? submittedDate <= toDate : true;

      return bySearch && byHospital && byCompany && byFrom && byTo;
    });
  }, [invoices, search, hospital, company, dateFrom, dateTo]);

  const resetFilters = () => {
    setSearch("");
    setHospital("");
    setCompany("");
    setDateFrom("");
    setDateTo("");
  };

  return (
    <div className="space-y-5">
      <AuditorDashboardInvoiceFilters
        search={search}
        hospital={hospital}
        company={company}
        dateFrom={dateFrom}
        dateTo={dateTo}
        onSearchChange={setSearch}
        onHospitalChange={setHospital}
        onCompanyChange={setCompany}
        onDateFromChange={setDateFrom}
        onDateToChange={setDateTo}
        onReset={resetFilters}
      />
      <AuditorSubmittedInvoicesTable rows={filteredInvoices} />
    </div>
  );
}
